import { useState } from "react";
import { BadgeCheck } from "lucide-react";
import { HubProfilePopup } from "@/components/social/HubProfilePopup";

import { cn } from "@/lib/utils";
import { HubAvatar } from "@/components/social/HubAvatar";
import type { HubDisplayIdentity } from "@/hooks/hub/useHubIdentities";
import { hubBadgeReplacesRole, hubBadgeStyle } from "@/lib/hub/hubBadges";

/**
 * LR Hub (/social) feed author display — the SINGLE place where a feed
 * author's identity is rendered (name, company, role badge, verified check,
 * avatar). Every post and comment must use this component so that a future
 * anonymity toggle (e.g. a shipper appearing as "Shipper Anon") can be added
 * in exactly one place.
 *
 * IDENTITY SOURCE: when a `hub_settings` row exists for the author, its
 * display name + @handle / role-visibility / APPROVED avatar win over anything passed in.
 * Pass the row through `hubIdentity` (see `useHubIdentities`) — pending or
 * rejected photos are never returned by that RPC, so they can't leak here.
 *
 * PRIVACY — CRITICAL:
 * The LR Hub is a PUBLIC-IDENTITY surface. LR Direct is a BLIND-BID surface.
 * Do NOT import this component into any LR Direct bid / quote / counterparty
 * view, and do NOT reuse LR Direct identity/avatar components here.
 */

export type HubAuthorRole = "shipper" | "broker" | "carrier" | string;

export interface HubAuthor {
  id?: string | null;
  /** Display name (already resolved; pass the anon label here later). */
  name: string;
  /** Company display name (legal suffix already stripped by the caller). */
  company?: string | null;
  role?: HubAuthorRole | null;
  /** Legacy/public image URL fallback when the member has no Hub photo. */
  avatarUrl?: string | null;
  /**
   * Verified badge source of truth: `companies.documents_verified_at`
   * (non-null = verified) for every role. See notes in the header of the
   * feed hook — the profile itself carries no verification flag.
   */
  verified?: boolean;
  /** Reserved for the future anonymity feature. */
  anonymous?: boolean;
  /** Resolved LR Hub identity (username, role visibility, approved photo). */
  hubIdentity?: HubDisplayIdentity | null;
}

const SIZES = {
  sm: { avatar: "sm" as const, name: "text-[11.5px]", meta: "text-[10px]" },
  md: { avatar: "md" as const, name: "text-[12.5px]", meta: "text-[10.5px]" },
} as const;

/** Primary name shown in the feed: Hub display name wins, then the passed name. */
export function hubDisplayName(author: HubAuthor): string {
  return author.hubIdentity?.display_name || author.name;
}

/** Secondary @handle shown next to the display name (null when no Hub row). */
export function hubDisplayHandle(author: HubAuthor): string | null {
  return author.hubIdentity?.handle ? `@${author.hubIdentity.handle}` : null;
}

/** Role shown in the feed: hidden when the member turned "show my role" off. */
/**
 * Special Hub badge (Founder / Founding Member). Admin-assigned only and read
 * LIVE from the display-identity path, like role visibility.
 */
export function hubDisplayBadge(author: HubAuthor): string | null {
  return author.hubIdentity?.hub_badge ?? null;
}

export function hubDisplayRole(author: HubAuthor): string | null {
  if (author.hubIdentity) return author.hubIdentity.role;
  return author.role ?? null;
}

export function HubAuthorAvatar({
  author,
  size = "md",
  className,
}: {
  author: HubAuthor;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const s = SIZES[size];
  const hubPath = author.hubIdentity?.avatar_path ?? null;

  // Legacy/public URL path (mock + pre-Hub data) only when no Hub photo exists.
  if (!hubPath && author.avatarUrl) {
    return (
      <img
        src={author.avatarUrl}
        alt={hubDisplayName(author)}
        loading="lazy"
        className={cn(
          "flex-shrink-0 rounded-full object-cover",
          size === "sm" ? "h-8 w-8" : "h-10 w-10",
          className,
        )}
      />
    );
  }

  return (
    <HubAvatar
      username={author.hubIdentity?.display_name || author.name}
      avatarPath={hubPath}
      size={s.avatar}
      className={className}
    />
  );
}


export function HubAuthorDisplay({
  author,
  timeLabel,
  size = "md",
  trailing,
  className,
}: {
  author: HubAuthor;
  /** e.g. "2h ago" */
  timeLabel?: string;
  size?: keyof typeof SIZES;
  trailing?: React.ReactNode;
  className?: string;
}) {
  const s = SIZES[size];
  const name = hubDisplayName(author);
  const handle = hubDisplayHandle(author);
  const role = hubDisplayRole(author);
  const badge = hubBadgeStyle(hubDisplayBadge(author));
  const showRolePill = !!role && !hubBadgeReplacesRole(hubDisplayBadge(author));
  // Avatar click → read-only Hub quick-profile popup (Hub-isolated).
  const [profileOpen, setProfileOpen] = useState(false);
  const canOpenProfile = !!author.id;
  return (
    <div className={cn("flex min-w-0 items-center gap-2.5", className)}>
      {canOpenProfile ? (
        <>
          <button
            type="button"
            aria-label={`View ${name}'s Hub profile`}
            onClick={() => setProfileOpen(true)}
            className="flex-shrink-0 rounded-full transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal-deep/50"
          >
            <HubAuthorAvatar author={author} size={size} />
          </button>
          <HubProfilePopup userId={author.id ?? null} open={profileOpen} onOpenChange={setProfileOpen} />
        </>
      ) : (
        <HubAuthorAvatar author={author} size={size} />
      )}

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={cn("font-bold text-foreground", s.name)}>{name}</span>
          {handle ? (
            <span className={cn("text-muted-foreground", s.meta)}>{handle}</span>
          ) : null}
          {author.verified ? (
            <BadgeCheck className="h-3.5 w-3.5 flex-shrink-0 text-brand-teal-deep" aria-label="Verified" />
          ) : null}
          {badge ? (
            <span
              title={badge.title}
              className={cn(
                "rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide",
                badge.className,
              )}
            >
              {badge.label}
            </span>
          ) : null}
          {showRolePill ? (
            <span className="rounded bg-brand-teal-subtle px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-brand-teal-deep">
              {role}
            </span>
          ) : null}
        </div>

        <div className={cn("mt-0.5 flex items-center gap-1.5 text-muted-foreground", s.meta)}>
          {author.company ? <span className="truncate">{author.company}</span> : null}
          {author.company && timeLabel ? <span>·</span> : null}
          {timeLabel ? <span>{timeLabel}</span> : null}
          {trailing}
        </div>
      </div>
    </div>
  );
}

export default HubAuthorDisplay;
